import "./lib/polyfills";
import { createRoot } from "react-dom/client";
import { i18nReady } from "./i18n/config";
import App from "./App.tsx";
import "./index.css";
import { initWebVitals } from "./lib/webVitals";
import { installErrorTracking } from "./lib/errorReporter";
import { Capacitor } from "@capacitor/core";
import { getRuntimeEnv, isNonProductionRuntime } from "./lib/runtimeEnv";
import { clearPwaRuntimeState, retirePwaRuntimeForReleaseStability } from "./lib/pwaRecovery";
import { initializeProductAnalytics } from "./lib/productAnalytics";
import { hasPublicSupabaseConfig } from "./lib/publicSupabaseConfig";

// SW реєстрація і UI оновлення винесені в <UpdatePrompt /> всередині App,
// щоб користувач бачив банер про нову версію замість тихого автокешу.

// Real-User-Monitoring of Core Web Vitals — sent to web_vitals table.
if (typeof window !== "undefined") {
  const backendConfigured = hasPublicSupabaseConfig();
  if (Capacitor.isNativePlatform()) {
    document.documentElement.classList.add(`capacitor-${Capacitor.getPlatform()}`);
    document.documentElement.classList.add("capacitor-native");
  }
  document.documentElement.dataset.appEnv = getRuntimeEnv();
  if (isNonProductionRuntime()) {
    document.documentElement.classList.add("app-env-nonproduction");
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, nofollow");
  }

  if (backendConfigured) {
    // Install error tracker immediately to catch early errors.
    installErrorTracking();
    initializeProductAnalytics();
    // Defer non-critical telemetry init to idle.
    if (!window.location.pathname.startsWith("/admin")) {
      const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1500));
      idle(() => initWebVitals());
    }
  } else {
    console.warn("[staging] Supabase is not configured; telemetry remains disabled.");
  }
}

// Stale-chunk handling lives in installErrorTracking() (errorReporter.ts) —
// kept in one place to avoid duplicate reloads.

// Give the active language bundle a short window to load. Native shells need a
// faster first paint than the public web because the OS already provided chrome.
const rootElement = document.getElementById("root");
let appMounted = false;
const isNativeShell = typeof window !== "undefined" && Capacitor.isNativePlatform();
const isAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
const adminRuntimeReady = isAdminRoute && !isNativeShell
  ? clearPwaRuntimeState()
  : Promise.resolve();
const pwaRuntimeReady = retirePwaRuntimeForReleaseStability();

const mountApp = () => {
  if (!rootElement || appMounted) return;

  appMounted = true;
  createRoot(rootElement).render(<App />);
};

const i18nMountFallback = window.setTimeout(mountApp, isNativeShell ? 350 : 1800);

i18nReady
  .catch((error) => {
    console.error("[i18n] Initial language load failed", error);
  })
  .then(() => Promise.all([adminRuntimeReady, pwaRuntimeReady]))
  .catch((error) => {
    console.warn("[pwa] Admin cache cleanup failed", error);
  })
  .finally(() => {
    window.clearTimeout(i18nMountFallback);
    mountApp();
  });
